/**
 * Every date the client area shows a customer is rendered here, and nowhere else.
 *
 * ── Changing the format ──────────────────────────────────────────────────────
 * Edit DATE_FORMAT / DATE_TIME_FORMAT below. Nothing else in the app needs to change:
 * the two patterns are the only place a date's shape is written down.
 *
 *   DATE_FORMAT = 'DD.MM.YYYY'        -> 11.09.2026
 *   DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm' -> 11.09.2026 13:03
 *
 * Supported tokens: YYYY, YY, MM, DD, HH, mm, ss. Anything else in the pattern is
 * punctuation and is copied through as written.
 */

/** A date on its own: an invoice date, a due date, a registration date, a domain expiry. */
export const DATE_FORMAT = 'YYYY-MM-DD'

/** A date with a clock time: a ticket reply, a sent email, a transaction, an hourly usage row. */
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm'

/** Drawn in place of a date the backend has not set. */
export const EMPTY_DATE = '-'

type DateParts = {
  YYYY: string
  YY: string
  MM: string
  DD: string
  HH: string
  mm: string
  ss: string
  hasTime: boolean
  hasSeconds: boolean
}

/** Billing's "no date on file". It parses as a real string but is not a real date. */
const UNSET_DATE = '0000-00-00'

/** `2026-09-11`, optionally followed by `T`/space and `13:03[:08]` — what the API sends. */
const ISO_LIKE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/

/** `28/09/2026` — day-first, the shape billing writes inside invoice line descriptions. */
const DAY_FIRST = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/

/** A letter in the value means a month written out (`March 5, 2026`), not digits to be guessed at. */
const HAS_MONTH_NAME = /[a-z]/i

const pad = (value: number | string, length = 2) => String(value).padStart(length, '0')

const toParts = (
  year: number | string,
  month: number | string,
  day: number | string,
  hours?: number | string,
  minutes?: number | string,
  seconds?: number | string,
): DateParts => ({
  YYYY: pad(year, 4),
  YY: pad(year, 4).slice(-2),
  MM: pad(month),
  DD: pad(day),
  HH: pad(hours ?? 0),
  mm: pad(minutes ?? 0),
  ss: pad(seconds ?? 0),
  hasTime: hours !== undefined,
  hasSeconds: seconds !== undefined,
})

/**
 * Reads every date shape the backend sends into plain parts.
 *
 * An ISO-like value is read literally rather than through `new Date()`: reparsing it would
 * reintroduce the timezone shift that turns a UTC-midnight date into the day before for any
 * reader west of UTC. Everything else falls back to `Date`, which reads it in local time.
 */
const parseDate = (value: string): DateParts | null => {
  const raw = value?.trim()
  if (!raw) return null

  const iso = ISO_LIKE.exec(raw)
  if (iso) {
    const [, year, month, day, hours, minutes, seconds] = iso
    if (`${year}-${month}-${day}` === UNSET_DATE) return null
    return toParts(year, month, day, hours, minutes, seconds)
  }

  const dayFirst = DAY_FIRST.exec(raw)
  if (dayFirst) {
    const [, day, month, year] = dayFirst
    if (Number(month) < 1 || Number(month) > 12) return null
    return toParts(year, month, day)
  }

  // `new Date()` guesses at an all-numeric date it was not given in a shape recognised above:
  // `11.09.2026` comes back as 9 November, not 11 September. A visible "-" is a bug someone
  // fixes; a date silently shifted by two months is one nobody notices. A written-out date
  // carries no such ambiguity, so only those reach the fallback.
  if (!HAS_MONTH_NAME.test(raw)) return null

  const parsed = new Date(raw)
  if (isNaN(parsed.getTime())) return null

  return toParts(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate(),
    parsed.getHours(),
    parsed.getMinutes(),
    parsed.getSeconds(),
  )
}

const TOKENS = /YYYY|YY|MM|DD|HH|mm|ss/g

const render = (parts: DateParts, pattern: string): string =>
  pattern.replace(TOKENS, token => parts[token as keyof Omit<DateParts, 'hasTime' | 'hasSeconds'>])

/**
 * A date on its own, in DATE_FORMAT. Any time of day the value carries is dropped.
 *
 * Returns EMPTY_DATE for a missing, unset or unreadable value, so a column never prints
 * a raw `0000-00-00` at a customer.
 */
export const formatDate = (value: string | null | undefined): string => {
  const parts = parseDate(value ?? '')
  return parts ? render(parts, DATE_FORMAT) : EMPTY_DATE
}

/**
 * A date with its clock time, in DATE_TIME_FORMAT.
 *
 * A value that carries no time of day falls back to DATE_FORMAT rather than inventing a
 * midnight that was never recorded — so a mixed column (daily and hourly usage rows, say)
 * stays readable without the caller having to know which it is holding.
 */
export const formatDateTime = (value: string | null | undefined): string => {
  const parts = parseDate(value ?? '')
  if (!parts) return EMPTY_DATE

  return render(parts, parts.hasTime ? DATE_TIME_FORMAT : DATE_FORMAT)
}

/** Every date shape `parseDate` understands, as it appears in the middle of a sentence. */
const DATE_IN_TEXT = /\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?|\d{1,2}\/\d{1,2}\/\d{4}/g

/**
 * Rewrites the dates found inside free text, leaving the rest of the line untouched.
 *
 * For the places where a date arrives buried in prose rather than in a field of its own —
 * billing writes an invoice line's period into the description, as "Medium VM (02/01/2026 -
 * 03/01/2026)". A fragment that is not a date survives intact, so a line holding a spec
 * ("vCPU: 4 vCPU") passes straight through.
 */
export const formatDatesInText = (text: string): string => {
  if (!text) return text

  return text.replace(DATE_IN_TEXT, match => {
    const parts = parseDate(match)
    return parts ? render(parts, parts.hasTime ? DATE_TIME_FORMAT : DATE_FORMAT) : match
  })
}

/**
 * The machine-readable form for a `<time datetime="...">` attribute: ISO 8601, always.
 *
 * Deliberately the one thing here that does NOT follow DATE_FORMAT. That attribute is read by
 * assistive technology and by anything parsing the page, and the HTML spec fixes its shape — so
 * changing how the client area *shows* a date must never change what it *says* a date is.
 * The visible text beside it still comes from formatDate / formatDateTime.
 *
 * Keeps every part the source recorded, seconds included. The point of the pairing is that the
 * visible text is the shortened one — the attribute is where the full precision survives.
 */
export const toMachineDate = (value: string | null | undefined): string | undefined => {
  const parts = parseDate(value ?? '')
  if (!parts) return undefined

  const date = `${parts.YYYY}-${parts.MM}-${parts.DD}`
  if (!parts.hasTime) return date

  const time = `${parts.HH}:${parts.mm}`
  return parts.hasSeconds ? `${date}T${time}:${parts.ss}` : `${date}T${time}`
}
